import React, { useState, useEffect } from "react";
import {
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
} from "react-icons/fa";
import { supabase } from "../../supabaseClient";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import InlineAlert from "../UI/DesignStandards/Notifications/InlineAlert";
import { formatPhoneNumber } from "../../utility/forms/formInputFormat";

const TournamentContactInfo = ({
  tournamentId,
  className = "card-board p-6",
  showHeading = true,
}) => {
  const [contacts, setContacts] = useState([]);
  const [links, setLinks] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const clearError = () => setError(null);

  useEffect(() => {
    if (tournamentId) {
      loadOrganizationContactInfo();
    }
  }, [tournamentId]);

  const loadOrganizationContactInfo = async () => {
    if (!tournamentId) return;

    setLoading(true);
    try {
      setError(null);
      // First, get the tournament's organization ID
      const { data: tournament, error: tournamentError } = await supabase
        .from("tournaments")
        .select("organizationID")
        .eq("id", tournamentId)
        .single();

      if (tournamentError) throw tournamentError;

      if (!tournament.organizationID) {
        setLoading(false);
        return;
      }

      // Load organization details
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .select("id, name, nameAbbreviation, description")
        .eq("id", tournament.organizationID)
        .single();

      if (orgError) throw orgError;

      setOrganization(orgData);

      // Load organization contacts
      const { data: contactData, error: contactError } = await supabase
        .from("organizationContact")
        .select("*")
        .eq("organizationID", tournament.organizationID)
        .is("deletedAt", null)
        .order("name");

      if (contactError) throw contactError;

      setContacts(contactData || []);

      // Load organization links
      const { data: linkData, error: linkError } = await supabase
        .from("organizationLinks")
        .select("*")
        .eq("organizationID", tournament.organizationID)
        .is("deletedAt", null)
        .order("type");

      if (linkError) throw linkError;

      setLinks(linkData || []);
    } catch (error) {
      console.error("Error loading organization contact info:", error);
      setError("Failed to load contact information");
    } finally {
      setLoading(false);
    }
  };

  const getLinkIcon = (type) => {
    const typeLower = type.toLowerCase();
    if (typeLower.includes("facebook")) return FaFacebook;
    if (typeLower.includes("twitter")) return FaTwitter;
    if (typeLower.includes("instagram")) return FaInstagram;
    if (typeLower.includes("linkedin")) return FaLinkedin;
    if (typeLower.includes("website") || typeLower.includes("web"))
      return FaGlobe;
    return FaGlobe;
  };

  const getLinkColor = () => "text-brand-gold-ink hover:underline";

  if (loading) {
    return (
      <div className={className}>
        <div className="animate-pulse">
          <div className="h-6 bg-brand-charcoal/10 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-brand-charcoal/10 rounded"></div>
            <div className="h-4 bg-brand-charcoal/10 rounded"></div>
            <div className="h-4 bg-brand-charcoal/10 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className={className}>
        {showHeading && (
          <h3 className="heading-display text-xl mb-4">Contact</h3>
        )}
        <p className="text-sm text-brand-muted">
          No contact information available
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      {showHeading && (
        <h3 className="heading-display text-xl mb-4">Contact</h3>
      )}

      {/* Error Display */}
      {error && (
        <InlineAlert type="error" message={error} onClose={clearError} />
      )}

      {/* Organization Info */}
      <div className="mb-4 pb-4 border-b border-brand-charcoal/15">
        <h4 className="font-display font-bold text-brand-charcoal mb-2">
          {organization.name}
          {organization.nameAbbreviation && (
            <span className="text-brand-muted ml-2">
              ({organization.nameAbbreviation})
            </span>
          )}
        </h4>
        {organization.description && (
          <p className="text-sm text-brand-muted">{organization.description}</p>
        )}
      </div>

      {/* Contacts */}
      {contacts.length > 0 && (
        <div className="mb-4">
          <h5 className="text-sm font-medium text-brand-charcoal mb-3">Contacts</h5>
          <div className="divide-y divide-brand-charcoal/10">
            {contacts.map((contact) => (
              <div key={contact.id} className="py-3">
                {contact.name && (
                  <div className="font-medium text-brand-charcoal text-sm mb-1">
                    {contact.name}
                    {contact.organizationRole && (
                      <span className="text-brand-muted ml-2 text-xs">
                        ({contact.organizationRole})
                      </span>
                    )}
                  </div>
                )}
                <div className="space-y-1">
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}`}
                      className="flex items-center text-sm text-brand-gold-ink hover:underline"
                    >
                      <FaEnvelope className="mr-2 h-3 w-3" />
                      {contact.email}
                    </a>
                  )}
                  {contact.phone && (
                    <a
                      href={`tel:${contact.phone}`}
                      className="flex items-center text-sm text-brand-gold-ink hover:underline"
                    >
                      <FaPhone className="mr-2 h-3 w-3" />
                      {formatPhoneNumber(contact.phone)}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Links */}
      {links.length > 0 && (
        <div>
          <h5 className="text-sm font-medium text-brand-charcoal mb-3">Links</h5>
          <div className="space-y-2">
            {links.map((link) => {
              const IconComponent = getLinkIcon(link.type);
              const linkColor = getLinkColor();

              return (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center text-sm ${linkColor} hover:underline`}
                >
                  <IconComponent className="mr-2 h-3 w-3" />
                  {link.type}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {contacts.length === 0 && links.length === 0 && (
        <div className="text-center py-4">
          <p className="text-sm text-brand-muted">
            No contact information available
          </p>
        </div>
      )}
    </div>
  );
};

export default TournamentContactInfo;
